import { getFirebaseToken } from '../firebase';

/**
 * Service for FarmRoad AI Insights Historization API
 * Handles saving, retrieving, and managing AI analysis history
 */
class FarmRoadInsightsService {
  constructor() {
    this.baseUrl = process.env.REACT_APP_BACKEND_URL || 'http://localhost:8080';
    this.apiPrefix = '/api/farmroad';
  }

  /**
   * Get headers with Firebase authentication token
   */
  async _headers() {
    const token = await getFirebaseToken();
    return {
      'Authorization': `Bearer ${token}`,
      'Content-Type': 'application/json'
    };
  }

  /**
   * Save a new AI analysis to history
   * @param {Object} analysisData - Complete analysis data
   * @returns {Promise<Object>} Saved insight with ID
   */
  async saveAnalysis(analysisData) {
    try {
      const response = await fetch(`${this.baseUrl}${this.apiPrefix}/insights/analyze`, {
        method: 'POST',
        headers: await this._headers(),
        body: JSON.stringify(analysisData)
      });

      if (!response.ok) {
        const error = await response.json();
        throw new Error(error.error || 'Failed to save analysis');
      }

      const result = await response.json();
      console.log('[InsightsService] Analysis saved:', result.data.insight_id);
      return result.data;
    } catch (error) {
      console.error('[InsightsService] Error saving analysis:', error);
      throw error;
    }
  }

  /**
   * Get analysis history for a farm with filters
   * @param {string} farmId - Farm ID
   * @param {Object} filters - Optional filters
   * @returns {Promise<Object>} Paginated history with insights and pagination info
   */
  async getHistory(farmId, filters = {}) {
    try {
      const params = new URLSearchParams({
        ...(filters.sectorId && { sectorId: filters.sectorId }),
        ...(filters.compartmentId && { compartmentId: filters.compartmentId }),
        ...(filters.culture && { culture: filters.culture }),
        ...(filters.analysisPeriod && { analysisPeriod: filters.analysisPeriod }),
        ...(filters.startDate && { startDate: filters.startDate }),
        ...(filters.endDate && { endDate: filters.endDate }),
        ...(filters.overallStatus && { overallStatus: filters.overallStatus }),
        ...(filters.minCriticalCount && { minCriticalCount: filters.minCriticalCount }),
        ...(filters.maxCriticalCount && { maxCriticalCount: filters.maxCriticalCount }),
        ...(filters.search && { search: filters.search }),
        limit: filters.limit || 50,
        offset: filters.offset || 0,
        sortBy: filters.sortBy || 'created_at',
        sortOrder: filters.sortOrder || 'DESC'
      });

      const response = await fetch(`${this.baseUrl}${this.apiPrefix}/insights/history/${farmId}?${params}`, {
        method: 'GET',
        headers: await this._headers()
      });

      if (!response.ok) {
        const error = await response.json();
        throw new Error(error.error || 'Failed to fetch history');
      }

      const result = await response.json();
      console.log('[InsightsService] History fetched:', result.pagination);
      return result;
    } catch (error) {
      console.error('[InsightsService] Error fetching history:', error);
      throw error;
    }
  }

  /**
   * Get detailed analysis by ID
   * @param {string} insightId - Insight ID
   * @returns {Promise<Object>} Full analysis details
   */
  async getAnalysis(insightId) {
    try {
      const response = await fetch(`${this.baseUrl}${this.apiPrefix}/insights/${insightId}`, {
        method: 'GET',
        headers: await this._headers()
      });

      if (!response.ok) {
        const error = await response.json();
        throw new Error(error.error || 'Failed to fetch analysis');
      }

      const result = await response.json();
      console.log('[InsightsService] Analysis fetched:', insightId);
      return result.data;
    } catch (error) {
      console.error('[InsightsService] Error fetching analysis:', error);
      throw error;
    }
  }

  /**
   * Delete an analysis (soft delete)
   * @param {string} insightId - Insight ID to delete
   * @returns {Promise<Object>} Deletion result
   */
  async deleteAnalysis(insightId) {
    try {
      const response = await fetch(`${this.baseUrl}${this.apiPrefix}/insights/${insightId}`, {
        method: 'DELETE',
        headers: await this._headers()
      });

      if (!response.ok) {
        const error = await response.json();
        throw new Error(error.error || 'Failed to delete analysis');
      }

      const result = await response.json();
      console.log('[InsightsService] Analysis deleted:', insightId);
      return result;
    } catch (error) {
      console.error('[InsightsService] Error deleting analysis:', error);
      throw error;
    }
  }

  /**
   * Get statistics for a farm's analyses
   * @param {string} farmId - Farm ID
   * @param {Object} filters - Optional filters
   * @returns {Promise<Object>} Statistics summary
   */
  async getStats(farmId, filters = {}) {
    try {
      const params = new URLSearchParams({
        ...(filters.startDate && { startDate: filters.startDate }),
        ...(filters.endDate && { endDate: filters.endDate }),
        ...(filters.sectorId && { sectorId: filters.sectorId }),
        ...(filters.culture && { culture: filters.culture }),
        ...(filters.analysisPeriod && { analysisPeriod: filters.analysisPeriod })
      });

      const response = await fetch(`${this.baseUrl}${this.apiPrefix}/insights/stats/${farmId}?${params}`, {
        method: 'GET',
        headers: await this._headers()
      });

      if (!response.ok) {
        const error = await response.json();
        throw new Error(error.error || 'Failed to fetch statistics');
      }

      const result = await response.json();
      console.log('[InsightsService] Stats fetched for farm:', farmId);
      return result.data;
    } catch (error) {
      console.error('[InsightsService] Error fetching stats:', error);
      throw error;
    }
  }

  /**
   * Compare two analyses
   * @param {string} insightId1 - First insight ID
   * @param {string} insightId2 - Second insight ID
   * @returns {Promise<Object>} Comparison result
   */
  async compareAnalyses(insightId1, insightId2) {
    try {
      const response = await fetch(`${this.baseUrl}${this.apiPrefix}/insights/compare/${insightId1}/${insightId2}`, {
        method: 'GET',
        headers: await this._headers()
      });

      if (!response.ok) {
        const error = await response.json();
        throw new Error(error.error || 'Failed to compare analyses');
      }

      const result = await response.json();
      console.log('[InsightsService] Comparison completed:', insightId1, 'vs', insightId2);
      return result.data;
    } catch (error) {
      console.error('[InsightsService] Error comparing analyses:', error);
      throw error;
    }
  }

  /**
   * Update user notes for an analysis
   * @param {string} insightId - Insight ID
   * @param {string} notes - User notes
   * @returns {Promise<Object>} Update result
   */
  async updateNotes(insightId, notes) {
    try {
      const response = await fetch(`${this.baseUrl}${this.apiPrefix}/insights/${insightId}/notes`, {
        method: 'POST',
        headers: await this._headers(),
        body: JSON.stringify({ notes })
      });

      if (!response.ok) {
        const error = await response.json();
        throw new Error(error.error || 'Failed to update notes');
      }

      const result = await response.json();
      console.log('[InsightsService] Notes updated for:', insightId);
      return result;
    } catch (error) {
      console.error('[InsightsService] Error updating notes:', error);
      throw error;
    }
  }
}

export default new FarmRoadInsightsService();
